/**
 * New Relic "custom event" beacon — a keepalive send that survives the
 * handshake page's immediate navigation.
 *
 * WHY THIS EXISTS
 * ----------------
 * The handshake page emits a New Relic event and then redirects to Auth within
 * a few hundred ms. The NR browser agent only ships buffered events on a 30s
 * timer or a `visibilitychange:hidden` end-of-life harvest — neither fires
 * reliably on a programmatic top-level navigation — and the agent exposes no
 * flush/force-send API. Result: ~two-thirds of handshake PageActions never
 * leave the browser.
 *
 * This module bypasses the agent's buffer entirely and POSTs the event straight
 * to NR's browser ingest endpoint with `keepalive: true` (falling back to
 * `navigator.sendBeacon`). Keepalive requests are guaranteed by the browser to
 * complete across a navigation — the same mechanism the Kafka `/api/v1/events`
 * POST already relies on, which is why Kafka delivery is ~100% while NR is ~33%.
 *
 * It emits a first-class custom event type (`CheckoutUiEvents`), NOT a
 * `PageAction`, so it is queried as `FROM CheckoutUiEvents ... FACET name`.
 *
 * NOTE: this hand-rolls the agent's `/ins` payload shape (mirrors
 * @newrelic/browser-agent@1.308.0). It is a prototype — validate against real
 * ingest before relying on it, and re-check if the agent is upgraded.
 */

const NR_BEACON_HOST = "bam.nr-data.net";

/** The NRDB event type (table). Keep in sync with the funnel dashboard. */
export const CHECKOUT_UI_EVENT_TYPE = "CheckoutUiEvents";

type BeaconAttributes = Record<string, string | number | boolean | undefined>;

interface NrCredentials {
  licenseKey: string;
  applicationID: string;
}

function readCredentials(): NrCredentials | null {
  const licenseKey = import.meta.env.VITE_NEWRELIC_LICENSE_KEY as
    | string
    | undefined;
  const applicationID = import.meta.env.VITE_NEWRELIC_APPLICATION_ID as
    | string
    | undefined;
  if (!licenseKey || !applicationID) return null;
  return { licenseKey, applicationID };
}

/**
 * Builds the ingest URL: `https://bam.nr-data.net/ins/1/{licenseKey}?a={appId}`.
 * `a` (application id) scopes the event to the browser app; the license key in
 * the path authenticates. `sa=1` mirrors the agent's standalone flag.
 */
function buildIngestUrl({ licenseKey, applicationID }: NrCredentials): string {
  const params = new URLSearchParams({
    a: applicationID,
    sa: "1",
    rst: String(Math.floor(performance.now())),
  });
  return `https://${NR_BEACON_HOST}/ins/1/${licenseKey}?${params.toString()}`;
}

/**
 * Fire-and-forget custom event to New Relic that survives navigation.
 *
 * @param name  funnel event name, e.g. "CHECKOUT_HANDSHAKE_COMPLETED"
 * @param attributes flat, small key/values (NR caps ~255 attrs & value length)
 * @returns true if a send was dispatched, false if it could not be attempted
 */
export function sendCheckoutUiBeacon(
  name: string,
  attributes: BeaconAttributes = {},
): boolean {
  try {
    const creds = readCredentials();
    if (!creds) return false;

    const event: Record<string, string | number | boolean> = {
      eventType: CHECKOUT_UI_EVENT_TYPE,
      name,
      // Distinguishes this browser-emitted copy from the WFS server-side copy
      // (source="wfs"). Both carry the same checkout_id, so a funnel on
      // uniqueCount(checkout_id) counts once — the paths are redundant, not
      // additive. A caller may override via `attributes`.
      source: "fe_beacon",
      timestamp: Date.now(),
    };
    for (const [key, value] of Object.entries(attributes)) {
      if (value !== undefined && value !== "") event[key] = value;
    }

    const url = buildIngestUrl(creds);
    // The `/ins` endpoint expects a JSON body under the `ins` array.
    const body = JSON.stringify({ ins: [event] });

    // Prefer keepalive fetch (lets us set text/plain, matches the agent).
    if (typeof fetch === "function") {
      void fetch(url, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body,
        keepalive: true,
        // No credentials/cookies — this is a cross-origin telemetry beacon.
        credentials: "omit",
        mode: "cors",
      }).catch(() => {
        // Best-effort: a failed beacon must never affect the flow.
      });
      return true;
    }

    // Fallback for environments without keepalive fetch.
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      return navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }));
    }

    return false;
  } catch {
    // NR must never break the handshake flow.
    return false;
  }
}
